// Geometry of individual elements: bounds, hit tests, selection tests and transforms.
import type {
  CardElement, Endpoint, FrameElement, ImageElement, InkElement, LineElement, NoteElement, SepiaElement,
  ShapeElement, ShapeKind, StickerElement, TextElement
} from "../../shared/model";
import {
  type Box, type Vec, boxesIntersect, boxContains, center, clipToOutline, distToSegment, expandBox, pointInBox,
  pointInPolygon, rotate, rotatedBounds, rotatedCorners, segmentIntersectsBox, boxFromPoints
} from "./geometry";
import { linePolyline } from "./sketch";

// ---- ids ----

const ALPHA = "0123456789abcdefghijklmnopqrstuvwxyz";
export function newId(len = 14): string {
  const b = new Uint8Array(len);
  crypto.getRandomValues(b);
  let s = "";
  for (let i = 0; i < len; i++) s += ALPHA[b[i] % 36];
  return s;
}

export function newSeed(): number {
  return Math.floor(Math.random() * 2 ** 31);
}

// ---- bounds ----

export function boxOf(el: SepiaElement): Box {
  return { x: el.x, y: el.y, w: el.w, h: el.h };
}

/** World-space points of an ink stroke (rotation applied). */
export function inkWorldPoints(el: InkElement): Vec[] {
  const out: Vec[] = [];
  const c = { x: el.x + el.w / 2, y: el.y + el.h / 2 };
  for (let i = 0; i + 2 < el.pts.length; i += 3) {
    const p = { x: el.x + el.pts[i], y: el.y + el.pts[i + 1] };
    out.push(el.rot ? rotate(p, el.rot, c) : p);
  }
  return out;
}

const boundsCache = new WeakMap<SepiaElement, Box>();

/** Axis-aligned world bounds including stroke width, used for culling and selection frames. */
export function bounds(el: SepiaElement): Box {
  let b = boundsCache.get(el);
  if (b) return b;
  switch (el.type) {
    case "ink":
      b = expandBox(rotatedBounds(boxOf(el), el.rot), el.size / 2 + 1);
      break;
    case "line": {
      const pts = linePolyline(el);
      b = expandBox(boxFromPoints(pts), el.strokeWidth * 2 + (el.start.head !== "none" || el.end.head !== "none" ? 12 : 2));
      break;
    }
    case "frame":
      b = rotatedBounds({ x: el.x, y: el.y - 34, w: el.w, h: el.h + 34 }, el.rot);
      break;
    default:
      b = rotatedBounds(boxOf(el), el.rot);
  }
  boundsCache.set(el, b);
  return b;
}

/** Bounds without stroke padding (what selection handles wrap). */
export function frameBox(el: SepiaElement): Box {
  if (el.type === "line") return boxFromPoints(linePolyline(el));
  return boxOf(el);
}

// ---- shape outlines for hit tests ----

export function shapePolygon(kind: ShapeKind, w: number, h: number): Vec[] {
  const ellipse = (n = 40) =>
    Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2;
      return { x: w / 2 + (Math.cos(a) * w) / 2, y: h / 2 + (Math.sin(a) * h) / 2 };
    });
  switch (kind) {
    case "ellipse":
    case "cloud":
    case "heart":
      return ellipse();
    case "diamond":
      return [{ x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 }];
    case "triangle":
      return [{ x: w / 2, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
    case "hexagon":
      return [{ x: w * 0.25, y: 0 }, { x: w * 0.75, y: 0 }, { x: w, y: h / 2 }, { x: w * 0.75, y: h }, { x: w * 0.25, y: h }, { x: 0, y: h / 2 }];
    case "parallelogram":
      return [{ x: w * 0.2, y: 0 }, { x: w, y: 0 }, { x: w * 0.8, y: h }, { x: 0, y: h }];
    case "star": {
      const pts: Vec[] = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 0.5 : 0.21;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push({ x: w / 2 + Math.cos(a) * r * w, y: h * 0.55 + Math.sin(a) * r * h * 1.1 });
      }
      return pts;
    }
    default:
      return [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
  }
}

function toLocal(el: SepiaElement, p: Vec): Vec {
  return el.rot ? rotate(p, -el.rot, center(boxOf(el))) : p;
}

function distToPolyline(p: Vec, pts: Vec[]): number {
  if (pts.length === 1) return Math.hypot(p.x - pts[0].x, p.y - pts[0].y);
  let d = Infinity;
  for (let i = 1; i < pts.length; i++) d = Math.min(d, distToSegment(p, pts[i - 1], pts[i]));
  return d;
}

function distToPolygonEdge(p: Vec, poly: Vec[]): number {
  let d = Infinity;
  for (let i = 0; i < poly.length; i++) d = Math.min(d, distToSegment(p, poly[i], poly[(i + 1) % poly.length]));
  return d;
}

/**
 * Does world point `p` touch the element? `tol` is in world units.
 * `precise` = only the painted parts (used by the eraser); otherwise hollow shapes also answer inside.
 */
export function hitTest(el: SepiaElement, p: Vec, tol: number, precise = false): boolean {
  if (!pointInBox(p, expandBox(bounds(el), tol))) return false;
  switch (el.type) {
    case "ink": {
      const lp = toLocal(el, p);
      const local: Vec[] = [];
      for (let i = 0; i + 2 < el.pts.length; i += 3) local.push({ x: el.x + el.pts[i], y: el.y + el.pts[i + 1] });
      return distToPolyline(lp, local) <= el.size / 2 + tol;
    }
    case "line":
      return distToPolyline(p, linePolyline(el)) <= el.strokeWidth / 2 + tol + 3;
    case "shape": {
      const lp = toLocal(el, p);
      const q = { x: lp.x - el.x, y: lp.y - el.y };
      const poly = shapePolygon(el.kind, el.w, el.h);
      const filled = el.fillStyle !== "none" || !!el.text.trim();
      if (!precise && (filled || (el.w < 80 && el.h < 80))) return pointInPolygon(q, poly) || distToPolygonEdge(q, poly) <= tol;
      return distToPolygonEdge(q, poly) <= el.strokeWidth / 2 + tol || (!precise && filled && pointInPolygon(q, poly));
    }
    case "frame": {
      const lp = toLocal(el, p);
      const titleBox = { x: el.x, y: el.y - 34, w: Math.min(el.w, 40 + el.title.length * 11), h: 34 };
      if (pointInBox(lp, titleBox)) return true;
      const q = { x: lp.x - el.x, y: lp.y - el.y };
      return !precise && distToPolygonEdge(q, shapePolygon("rect", el.w, el.h)) <= tol + 4;
    }
    default:
      return pointInBox(toLocal(el, p), boxOf(el));
  }
}

/** Inside the outline of a hollow shape (a weaker hit, used only when nothing painted is under the point). */
export function hitHollow(el: SepiaElement, p: Vec): boolean {
  if (el.type !== "shape") return false;
  if (!pointInBox(p, bounds(el))) return false;
  const lp = toLocal(el, p);
  return pointInPolygon({ x: lp.x - el.x, y: lp.y - el.y }, shapePolygon(el.kind, el.w, el.h));
}

/** Marquee selection: does the element meet `box`? Frames must be fully inside. */
export function intersectsBox(el: SepiaElement, box: Box): boolean {
  const b = bounds(el);
  if (!boxesIntersect(b, box)) return false;
  if (el.type === "frame") return boxContains(box, frameBox(el));
  if (boxContains(box, b)) return true;
  if (el.type === "ink") {
    const pts = inkWorldPoints(el);
    if (pts.length === 1) return pointInBox(pts[0], box);
    for (let i = 1; i < pts.length; i++) if (segmentIntersectsBox(pts[i - 1], pts[i], box)) return true;
    return false;
  }
  if (el.type === "line") {
    const pts = linePolyline(el);
    for (let i = 1; i < pts.length; i++) if (segmentIntersectsBox(pts[i - 1], pts[i], box)) return true;
    return false;
  }
  const corners = rotatedCorners(boxOf(el), el.rot);
  return corners.some((c) => pointInBox(c, box)) || pointInPolygon(center(box), corners);
}

/** Lasso selection: is the element mostly inside the polygon? */
export function insideLasso(el: SepiaElement, poly: Vec[]): boolean {
  if (!boxesIntersect(bounds(el), boxFromPoints(poly))) return false;
  let pts: Vec[];
  if (el.type === "ink") pts = inkWorldPoints(el);
  else if (el.type === "line") pts = linePolyline(el);
  else pts = [...rotatedCorners(boxOf(el), el.rot), center(boxOf(el))];
  if (!pts.length) return false;
  let inside = 0;
  for (const p of pts) if (pointInPolygon(p, poly)) inside++;
  return inside / pts.length >= (el.type === "ink" ? 0.5 : 0.6);
}

// ---- transforms ----

export type Xform =
  | { kind: "move"; dx: number; dy: number }
  | { kind: "rotate"; angle: number; origin: Vec }
  | { kind: "scale"; sx: number; sy: number; origin: Vec; frameRot: number };

function normRot(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r <= -Math.PI) r += Math.PI * 2;
  return Math.abs(r) < 1e-9 ? 0 : r;
}

function mapScale(p: Vec, t: Extract<Xform, { kind: "scale" }>): Vec {
  const q = rotate(p, -t.frameRot, t.origin);
  const s = { x: t.origin.x + (q.x - t.origin.x) * t.sx, y: t.origin.y + (q.y - t.origin.y) * t.sy };
  return rotate(s, t.frameRot, t.origin);
}

function withPoints(el: LineElement, world: Vec[]): LineElement {
  const b = boxFromPoints(world);
  return { ...el, x: b.x, y: b.y, w: Math.max(1, b.w), h: Math.max(1, b.h), pts: world.map((p) => [p.x - b.x, p.y - b.y] as [number, number]) };
}

function lineWorld(el: LineElement): Vec[] {
  return el.pts.map(([px, py]) => ({ x: el.x + px, y: el.y + py }));
}

function inkFromWorld(el: InkElement, world: Vec[], sizeScale: number): InkElement {
  const b = boxFromPoints(world);
  const pts: number[] = [];
  for (let i = 0; i < world.length; i++) pts.push(round2(world[i].x - b.x), round2(world[i].y - b.y), el.pts[i * 3 + 2]);
  return { ...el, x: b.x, y: b.y, w: Math.max(1, b.w), h: Math.max(1, b.h), rot: 0, pts, size: Math.max(0.5, el.size * sizeScale) };
}

export const round2 = (n: number) => Math.round(n * 100) / 100;

export function transformElement<T extends SepiaElement>(el: T, t: Xform): T {
  if (t.kind === "move") {
    if (!t.dx && !t.dy) return el;
    return { ...el, x: el.x + t.dx, y: el.y + t.dy };
  }

  if (t.kind === "rotate") {
    if (el.type === "line") return withPoints(el, lineWorld(el).map((p) => rotate(p, t.angle, t.origin))) as T;
    const c = rotate(center(boxOf(el)), t.angle, t.origin);
    return { ...el, x: c.x - el.w / 2, y: c.y - el.h / 2, rot: normRot(el.rot + t.angle) };
  }

  // scale
  const ax = Math.abs(t.sx);
  const ay = Math.abs(t.sy);
  const aligned = Math.abs(normRot(el.rot - t.frameRot)) < 1e-6;

  if (el.type === "line") return withPoints(el, lineWorld(el).map((p) => mapScale(p, t))) as T;

  if (el.type === "ink") {
    if (aligned) {
      const w = Math.max(1, el.w * ax);
      const h = Math.max(1, el.h * ay);
      const c = mapScale(center(boxOf(el)), t);
      const pts = el.pts.slice();
      for (let i = 0; i + 2 < pts.length; i += 3) {
        pts[i] = round2((pts[i] - el.w / 2) * t.sx + w / 2);
        pts[i + 1] = round2((pts[i + 1] - el.h / 2) * t.sy + h / 2);
      }
      return { ...el, x: c.x - w / 2, y: c.y - h / 2, w, h, pts, size: Math.max(0.5, el.size * Math.sqrt(ax * ay)) };
    }
    return inkFromWorld(el, inkWorldPoints(el).map((p) => mapScale(p, t)), Math.sqrt(ax * ay)) as T;
  }

  const c = mapScale(center(boxOf(el)), t);
  if (el.type === "text") {
    if (Math.abs(t.sy - 1) < 1e-6 && Math.abs(t.sx - 1) > 1e-6) {
      // side handle: re-wrap to a new width
      const w = Math.max(24, el.w * ax);
      return { ...el, x: c.x - w / 2, w, autoW: false };
    }
    const k = Math.abs(t.sy - 1) > 1e-6 ? ay : ax;
    const w = Math.max(8, el.w * ax);
    const h = Math.max(8, el.h * ay);
    return { ...el, x: c.x - w / 2, y: c.y - h / 2, w, h, fontSize: Math.max(4, round2(el.fontSize * k)) };
  }

  const minSide = el.type === "sticker" || el.type === "image" ? 12 : 16;
  const w = Math.max(minSide, el.w * ax);
  const h = Math.max(minSide, el.h * ay);
  return { ...el, x: c.x - w / 2, y: c.y - h / 2, w, h };
}

// ---- connectors ----

function outlineKind(el: SepiaElement): "rect" | "ellipse" | "diamond" {
  if (el.type === "shape") {
    if (el.kind === "ellipse" || el.kind === "cloud" || el.kind === "heart" || el.kind === "star") return "ellipse";
    if (el.kind === "diamond") return "diamond";
  }
  return "rect";
}

export function isCenterAnchor(e: Endpoint): boolean {
  return e.ax === undefined || e.ay === undefined || (Math.abs(e.ax - 0.5) < 0.2 && Math.abs(e.ay - 0.5) < 0.2);
}

/** Where a bound connector end sits on its target, seen from `toward`. */
export function boundPoint(target: SepiaElement, end: Endpoint, toward: Vec): Vec {
  const box = frameBox(target);
  const c = center(box);
  if (!isCenterAnchor(end)) {
    const p = { x: box.x + (end.ax ?? 0.5) * box.w, y: box.y + (end.ay ?? 0.5) * box.h };
    return rotate(p, target.rot, c);
  }
  const t = rotate(toward, -target.rot, c);
  return rotate(clipToOutline(box, t, outlineKind(target), 7), target.rot, c);
}

/** Recompute the ends of a connector from what it is attached to. */
export function routeConnector(line: LineElement, get: (id: string) => SepiaElement | undefined): LineElement {
  const pts = lineWorld(line);
  const n = pts.length;
  const startT = line.start.bind ? get(line.start.bind) : undefined;
  const endT = line.end.bind ? get(line.end.bind) : undefined;
  if (!startT && !endT) return line;
  const aim = (t: SepiaElement | undefined, e: Endpoint, fallback: Vec) =>
    t ? (isCenterAnchor(e) ? center(frameBox(t)) : boundPoint(t, e, fallback)) : fallback;
  const startAim = aim(startT, line.start, pts[0]);
  const endAim = aim(endT, line.end, pts[n - 1]);
  const next = [...pts];
  if (startT) next[0] = boundPoint(startT, line.start, n > 2 ? pts[1] : endAim);
  if (endT) next[n - 1] = boundPoint(endT, line.end, n > 2 ? pts[n - 2] : startAim);
  if (next.every((p, i) => Math.abs(p.x - pts[i].x) < 0.01 && Math.abs(p.y - pts[i].y) < 0.01)) return line;
  return withPoints(line, next);
}

// ---- text & links (search index, backlinks) ----

export function elementText(el: SepiaElement): string {
  switch (el.type) {
    case "text":
    case "note":
      return el.text;
    case "card":
      return el.md;
    case "shape":
      return el.text;
    case "line":
      return el.label;
    case "frame":
      return el.title;
    case "image":
      return el.caption || "";
    default:
      return "";
  }
}

const LINK_RE = /\[\[([^\]\n]{1,120})\]\]/g;

export function elementLinks(el: SepiaElement): string[] {
  const text = elementText(el);
  const out: string[] = [];
  for (const m of text.matchAll(LINK_RE)) out.push(m[1].split("|")[0].trim());
  return out;
}

export function isContainer(el: SepiaElement): el is FrameElement {
  return el.type === "frame";
}

export type TextLike = TextElement | NoteElement | CardElement | ShapeElement;
export type BoxLike = NoteElement | CardElement | ImageElement | StickerElement | FrameElement | ShapeElement | TextElement;
